import { config } from "@/lib/config";
import { getSupabase } from "@/lib/supabase";
import { activeFamilyId } from "@/lib/session";

/**
 * Push-Benachrichtigungen, je Gerät.
 *
 * Ausnahme von Regel 1: Diese Zeilen gehen nicht durch Dexie und nicht durch
 * die Outbox, sondern direkt nach Supabase. Eine Push-Anmeldung gehört einem
 * Browser auf einem Gerät, nicht der Familie. Sie hat keinen Nutzen auf einem
 * zweiten Gerät, sie überlebt keinen Wechsel, und sie muss nicht offline
 * funktionieren: Ohne Netz gibt es ohnehin nichts anzumelden. Der Zustand des
 * Schalters kommt vom Browser selbst (pushManager.getSubscription), nicht aus
 * einer Tabelle.
 */

export type PushZustand = "nicht-unterstützt" | "nicht-eingerichtet" | "verweigert" | "aus" | "an";

/** iOS liefert Push erst, wenn die App auf dem Startbildschirm liegt. */
export function alsAppInstalliert(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || "standalone" in navigator;
}

export function pushMoeglich(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function pushZustand(): Promise<PushZustand> {
  if (!pushMoeglich()) return "nicht-unterstützt";
  if (config().pushPublicKey.length === 0) return "nicht-eingerichtet";
  if (Notification.permission === "denied") return "verweigert";

  const registrierung = await navigator.serviceWorker.getRegistration();
  const anmeldung = await registrierung?.pushManager.getSubscription();
  return anmeldung ? "an" : "aus";
}

/**
 * base64url aus der Konfiguration in die Bytes, die der Browser erwartet.
 *
 * Rückgabe ist ein ArrayBuffer und keine Uint8Array-Sicht: Seit TypeScript 5.7
 * trägt Uint8Array den Puffertyp mit sich, und die Typen der Push-API nehmen
 * die Sicht dann nicht mehr an.
 */
function schluesselBytes(base64url: string): ArrayBuffer {
  const gefuellt = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), "=");
  const roh = atob(gefuellt.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Schlüssel des Geräts als base64url, ohne Füllzeichen.
 *
 * Nicht Standard-base64: Der Versand auf der anderen Seite dekodiert nach
 * RFC 4648 §5, und ein "+" oder "/" macht daraus Müll. Der Fehler fiele erst
 * beim ersten echten Push auf, und dann als stilles Nichts.
 */
function alsBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let roh = "";
  for (const byte of bytes) roh += String.fromCharCode(byte);
  return btoa(roh).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

/** Ein Name, an dem man das Gerät in der Liste wiedererkennt. */
function geraetename(): string {
  const kennung = navigator.userAgent;
  if (/iPhone/u.test(kennung)) return "iPhone";
  if (/iPad/u.test(kennung)) return "iPad";
  if (/Android/u.test(kennung)) return "Android";
  if (/Macintosh/u.test(kennung)) return "Mac";
  if (/Windows/u.test(kennung)) return "Windows";
  return "Gerät";
}

/**
 * Meldet dieses Gerät an. Fragt dabei um Erlaubnis, das geht nur aus einer
 * echten Nutzeraktion heraus, deshalb nie beim Start aufrufen.
 */
export async function pushAnmelden(): Promise<PushZustand> {
  if (!pushMoeglich()) return "nicht-unterstützt";

  const schluessel = config().pushPublicKey;
  if (schluessel.length === 0) return "nicht-eingerichtet";

  const erlaubnis = await Notification.requestPermission();
  if (erlaubnis !== "granted") return erlaubnis === "denied" ? "verweigert" : "aus";

  const registrierung = await navigator.serviceWorker.ready;
  const anmeldung =
    (await registrierung.pushManager.getSubscription()) ??
    (await registrierung.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: schluesselBytes(schluessel),
    }));

  const client = getSupabase();
  if (!client) return "an";

  const { data } = await client.auth.getUser();
  if (!data.user) return "an";

  // Ein Gerät, eine Zeile: endpoint ist in der Tabelle eindeutig. Wer die
  // Erlaubnis widerruft und neu erteilt, bekommt denselben Eintrag zurück.
  await client.from("push_subscriptions").upsert(
    {
      family_id: activeFamilyId(),
      user_id: data.user.id,
      endpoint: anmeldung.endpoint,
      p256dh: alsBase64Url(anmeldung.getKey("p256dh")),
      auth_key: alsBase64Url(anmeldung.getKey("auth")),
      device_label: geraetename(),
      channel: "web",
      enabled: true,
      deleted_at: null,
      client_updated_at: new Date().toISOString(),
    },
    { onConflict: "endpoint" },
  );

  return "an";
}

/** Meldet dieses Gerät ab, im Browser und in der Datenbank. */
export async function pushAbmelden(): Promise<PushZustand> {
  const registrierung = await navigator.serviceWorker.getRegistration();
  const anmeldung = await registrierung?.pushManager.getSubscription();
  if (!anmeldung) return "aus";

  const endpoint = anmeldung.endpoint;
  await anmeldung.unsubscribe();

  const client = getSupabase();
  if (client) {
    await client
      .from("push_subscriptions")
      .update({ enabled: false, deleted_at: new Date().toISOString(), client_updated_at: new Date().toISOString() })
      .eq("endpoint", endpoint);
  }

  return "aus";
}
